import React, { useEffect, useState } from 'react';
import { usuariosService, Usuario } from '../services/api/usuariosService';
import { authService } from '../services/authService';
import { usePermission } from '../hooks/usePermission';

interface FiltroPsicologoProps {
  value: number | null;
  onChange: (psicologoId: number | null) => void;
  incluirTodos?: boolean;
  label?: string;
  disabled?: boolean;
  className?: string;
}

/**
 * Componente para filtrar/selecionar psicólogo
 * Usado em listas de pacientes, consultas e relatórios
 */
export default function FiltroPsicologo({ 
  value, 
  onChange, 
  incluirTodos = true,
  label = 'Filtrar por Psicólogo',
  disabled = false,
  className = ''
}: FiltroPsicologoProps) {
  const [psicologos, setPsicologos] = useState<Usuario[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isPsicologo, setIsPsicologo] = useState<boolean>(false);
  const { tipoEmpresa } = usePermission();
  
  useEffect(() => {
    carregarPsicologos();
  }, []);
  
  const carregarPsicologos = async () => {
    try {
      setLoading(true);
      setError(null);
      // Se o usuário logado é psicólogo, listar apenas ele (evita erro de carga e cumpre regra)
      await authService.waitForInitialization();
      const usuarioAtual = authService.getUsuario();
      if (usuarioAtual?.cargo === 'psicologo') {
        const selfUsuario: Usuario = {
          id: String(usuarioAtual.id),
          nome: usuarioAtual.nome,
          usuario: usuarioAtual.nome, // placeholder
          email: '',
          cargo: 'psicologo',
          cod_empresa: usuarioAtual.cod_empresa,
          ativo: true
        };
        setIsPsicologo(true);
        setPsicologos([selfUsuario]);
        console.log('[FILTRO-PSICOLOGO] Modo psicólogo: carregado apenas o usuário logado');
        return;
      }

      // Caso contrário (admin/atendente/estudante): carregar lista completa
  const usuarios = await usuariosService.listarUsuarios();

      // Filtrar profissionais: psicólogos, estudantes (tratados como psicólogo) e admins ativos
      const psicologosList = usuarios.filter((u: Usuario) => {
        if (!u.ativo) return false;
        if (u.cargo === 'psicologo' || u.cargo === 'admin' || u.cargo === 'estudante') return true;
        return false;
      });

      // Ordenar: psicólogos primeiro, depois estudantes, depois admins, por nome
      psicologosList.sort((a: Usuario, b: Usuario) => {
        if (a.cargo === b.cargo) {
          return a.nome.localeCompare(b.nome);
        }
        const rank = (cargo: string) => cargo === 'psicologo' ? 0 : cargo === 'estudante' ? 1 : 2;
        return rank(a.cargo) - rank(b.cargo);
      });

      setPsicologos(psicologosList);

      console.log('[FILTRO-PSICOLOGO] Carregados:', psicologosList.length, 'psicólogos');
    } catch (error: any) {
      console.error('[FILTRO-PSICOLOGO] Erro ao carregar:', error);
      // Tratamento específico para 403: retornar apenas o próprio usuário (fallback)
      if (error?.response?.status === 403) {
        try {
          const usuarioAtual = authService.getUsuario();
          if (usuarioAtual) {
            const selfUsuario: Usuario = {
              id: String(usuarioAtual.id),
              nome: usuarioAtual.nome,
              usuario: usuarioAtual.nome,
              email: '',
              cargo: usuarioAtual.cargo as any,
              cod_empresa: usuarioAtual.cod_empresa,
              ativo: true
            };
            setPsicologos([selfUsuario]);
            setError(null);
            console.log('[FILTRO-PSICOLOGO] Fallback 403: carregado apenas usuário atual');
            return;
          }
        } catch {}
      }
      // Em vez de mensagem de erro agressiva, exibir lista vazia amigável
      setError(null);
      setPsicologos([]);
    } finally {
      setLoading(false);
    }
  };
  
  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newValue = e.target.value ? parseInt(e.target.value) : null;
    onChange(newValue);
  };
  
  return (
    <div className={`form-group ${className}`}>
      {label && <label>{tipoEmpresa === 'clinica' ? label.replace('Psicólogo', 'Profissional') : label}</label>}
      <select
        className="form-control"
        value={value || ''}
        onChange={handleChange}
        disabled={loading || disabled || isPsicologo}
      >
        {incluirTodos && !isPsicologo && (
          <option value="">{tipoEmpresa === 'clinica' ? 'Todos os Profissionais' : 'Todos os Psicólogos'}</option>
        )}
        
        {loading && <option value="">Carregando...</option>}
        
        {error && <option value="">Erro ao carregar</option>}
        
        {!loading && !error && psicologos.length === 0 && (
          <option value="">Nenhum psicólogo encontrado</option>
        )}
        
        {!loading && !error && psicologos.map(p => (
          <option key={p.id} value={p.id}>
            {p.nome} ({
              p.cargo === 'psicologo'
                ? 'Psicólogo'
                : p.cargo === 'estudante'
                  ? (tipoEmpresa === 'clinica' ? 'Estagiário' : 'Estudante')
                  : 'Admin'
            })
          </option>
        ))}
      </select>
      
      {error && (
        <small className="form-text text-danger">
          {error}
          <button 
            type="button" 
            className="btn btn-link btn-sm p-0 ml-2"
            onClick={carregarPsicologos}
          >
            Tentar novamente
          </button>
        </small>
      )}
    </div>
  );
}
